// "Quick Add" enrichment: everything we can learn from a video link using
// free public endpoints (oEmbed) and our own data.

import { Entity, Video } from "../types/cms";
import { parseVideoSource, ParsedVideoSource, youtubeThumbnail } from "./videoSources";
import { resolveXVideo } from "./xVideo";

export interface EnrichedVideo {
  source: ParsedVideoSource;
  title?: string;
  description?: string;
  authorName?: string;
  authorURL?: string;
  thumbnailURL?: string;
  durationSec?: number;
  releasedAt?: Date;
  /** For X posts: the resolved mp4. */
  mp4URL?: string;
  warnings: string[];
}

async function fetchJSON(url: string, timeoutMs = 8000): Promise<any | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** YouTube serves a 120×90 grey placeholder when maxres doesn't exist. */
function bestYouTubeThumbnail(id: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    const maxres = youtubeThumbnail(id, "maxresdefault");
    img.onload = () => resolve(img.naturalWidth > 120 ? maxres : youtubeThumbnail(id, "hqdefault"));
    img.onerror = () => resolve(youtubeThumbnail(id, "hqdefault"));
    img.src = maxres;
  });
}

export async function enrichVideoLink(rawURL: string): Promise<EnrichedVideo> {
  const source = parseVideoSource(rawURL);
  const result: EnrichedVideo = { source, warnings: [] };

  if (source.provider === "youtube" && source.id) {
    const [data, thumb] = await Promise.all([
      fetchJSON(`https://www.youtube.com/oembed?url=${encodeURIComponent(source.url)}&format=json`),
      bestYouTubeThumbnail(source.id),
    ]);
    result.thumbnailURL = thumb;
    if (data) {
      result.title = data.title;
      result.authorName = data.author_name;
      result.authorURL = data.author_url;
    } else {
      result.warnings.push("Vidéo privée, supprimée ou intégration désactivée : titre introuvable.");
    }
  } else if (source.provider === "vimeo") {
    const data = await fetchJSON(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(source.url)}&width=1280`,
    );
    if (data) {
      result.title = data.title;
      result.description = data.description || undefined;
      result.authorName = data.author_name;
      result.authorURL = data.author_url;
      result.durationSec = typeof data.duration === "number" ? data.duration : undefined;
      if (data.upload_date) {
        const d = new Date(String(data.upload_date).replace(" ", "T"));
        if (!isNaN(d.getTime())) result.releasedAt = d;
      }
      if (data.thumbnail_url) {
        result.thumbnailURL = String(data.thumbnail_url).replace(/_\d+(x\d+)?(?=$|\?)/, "_1280");
      }
    } else {
      result.warnings.push("Vimeo ne renvoie rien : la vidéo est peut-être privée.");
    }
  } else if (source.provider === "x" && source.id) {
    const x = await resolveXVideo(source.id);
    if (x) {
      result.mp4URL = x.mp4URL;
      result.thumbnailURL = x.posterURL;
    } else {
      result.warnings.push("Impossible de récupérer la vidéo de ce post X.");
    }
  } else if (source.provider === "linkedin") {
    result.warnings.push("LinkedIn ne partage pas les infos de ses vidéos : ajoutez le titre et une miniature à la main.");
  } else if (source.provider === "unknown") {
    result.warnings.push("Plateforme non reconnue : complétez la fiche à la main.");
  }

  return result;
}

// ---- Matching with existing brands / studios ------------------------------

export function normalizeName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\b(official|officiel|studio|studios|inc|sas|ltd|llc|hq|app|tv|the)\b/g, "")
    .replace(/[^a-z0-9]/g, "");
}

export function domainOf(url?: string): string | null {
  if (!url) return null;
  try {
    const u = new URL(/^https?:\/\//.test(url) ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return null;
  }
}

const IGNORED_DOMAINS = /(^|\.)(youtube\.com|youtu\.be|vimeo\.com|x\.com|twitter\.com|instagram\.com|linkedin\.com|facebook\.com|tiktok\.com|behance\.net|dribbble\.com|bit\.ly)$/;

export function domainsInText(text?: string): string[] {
  if (!text) return [];
  const found = text.match(/https?:\/\/[^\s)"'<>]+/g) ?? [];
  return Array.from(
    new Set(found.map(domainOf).filter((d): d is string => !!d && !IGNORED_DOMAINS.test(d))),
  );
}

export interface EntityMatch {
  entity: Entity;
  reason: string;
}

/** Finds an existing brand/studio from a channel name and links in the description. */
export function matchEntity(
  entities: Entity[],
  hints: { names?: (string | undefined)[]; domains?: string[] },
): EntityMatch | null {
  const domains = new Set(hints.domains ?? []);
  for (const entity of entities) {
    const d = domainOf(entity.websiteURL);
    if (d && domains.has(d)) return { entity, reason: `site ${d}` };
  }
  for (const rawName of hints.names ?? []) {
    if (!rawName) continue;
    const name = normalizeName(rawName);
    if (name.length < 2) continue;
    const exact = entities.find((e) => normalizeName(e.name) === name);
    if (exact) return { entity: exact, reason: `nom « ${rawName} »` };
    if (name.length >= 4) {
      const partial = entities.find((e) => {
        const n = normalizeName(e.name);
        return n.length >= 4 && (name.includes(n) || n.includes(name));
      });
      if (partial) return { entity: partial, reason: `nom proche de « ${rawName} »` };
    }
  }
  return null;
}

/** Title patterns like "Brand — Launch video" or "Launch video | Brand". */
export function brandNamesFromTitle(title?: string): string[] {
  if (!title) return [];
  return title
    .split(/\s[|–—-]\s|:\s|\sx\s|\s×\s/i)
    .map((part) => part.trim())
    .filter((part) => part.length > 1 && part.length < 40);
}

// ---- Duplicates --------------------------------------------------------------

export function sourceKeyOf(video: Pick<Video, "sourceKey" | "videoURL">): string | null {
  if (video.sourceKey) return video.sourceKey;
  return video.videoURL ? parseVideoSource(video.videoURL).sourceKey : null;
}

export function buildDuplicateIndex<T extends Pick<Video, "id" | "sourceKey" | "videoURL">>(
  videos: T[],
): Map<string, T> {
  const index = new Map<string, T>();
  videos.forEach((v) => {
    const key = sourceKeyOf(v);
    if (key) index.set(key, v);
  });
  return index;
}
